import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { formatUsd, formatDate, shortId } from '../lib/format';
import { SupportCasesTable } from '../components/SupportCasesTable';
import { StripeCustomerLink } from '../components/ExternalLinks';

interface CustomerDetailResponse {
  customer: {
    uuid: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    phone: string | null;
    stripe_customer_id: string | null;
    total_spent: number;
    order_count: number;
    first_seen_at: string;
    last_seen_at: string;
  };
  orders: Array<{
    uuid: string;
    total: number;
    payment_status: string;
    status: string;
    fulfillment_status: string;
    created_at: string;
    paid_at: string | null;
    shipped_at: string | null;
    stripe_checkout_session_id: string | null;
  }>;
}

export function CustomerDetail() {
  const { uuid } = useParams();
  const [data, setData] = useState<CustomerDetailResponse | null>(null);

  useEffect(() => {
    api
      .get<CustomerDetailResponse>(`/api/admin/customers/${uuid}`)
      .then(setData)
      .catch(console.error);
  }, [uuid]);

  if (!data) return <p>Loading…</p>;
  const { customer, orders } = data;
  const name = [customer.first_name, customer.last_name].filter(Boolean).join(' ');

  return (
    <div>
      <Link to="/customers" style={{ color: '#6b7280', fontSize: 13 }}>
        ← Customers
      </Link>
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 400, margin: '8px 0 4px' }}>
        {name || customer.email}
      </h1>
      <p style={{ color: '#6b7280', margin: '0 0 24px' }}>
        {customer.email}
        {customer.phone ? ` · ${customer.phone}` : ''}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
        <Stat label="Lifetime spend" value={formatUsd(customer.total_spent)} />
        <Stat label="Orders" value={String(customer.order_count)} />
        <Stat label="First seen" value={formatDate(customer.first_seen_at)} />
        <Stat label="Last seen" value={formatDate(customer.last_seen_at)} />
      </div>

      <h2 style={{ fontSize: 14, letterSpacing: 2, textTransform: 'uppercase', color: '#6b7280' }}>
        Order history
      </h2>
      <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, overflow: 'hidden' }}>
        {orders.length === 0 && (
          <p style={{ padding: 24, color: '#6b7280', margin: 0 }}>No orders.</p>
        )}
        {orders.map((o) => (
          <Link
            key={o.uuid}
            to={`/orders/${o.uuid}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr 120px 120px',
              gap: 16,
              padding: '14px 20px',
              borderBottom: '1px solid #f0f0f0',
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <span style={{ fontFamily: 'monospace', fontSize: 13 }}>#{shortId(o.uuid)}</span>
            <span style={{ fontSize: 13, color: '#6b7280' }}>{formatDate(o.created_at)}</span>
            <span style={{ fontSize: 13, color: '#6b7280' }}>{o.status}</span>
            <span style={{ fontSize: 14, textAlign: 'right' }}>{formatUsd(o.total)}</span>
          </Link>
        ))}
      </div>

      <h2 style={{ fontSize: 14, letterSpacing: 2, textTransform: 'uppercase', color: '#6b7280', marginTop: 32 }}>
        Support cases
      </h2>
      <SupportCasesTable params={new URLSearchParams({ customer: customer.uuid })} />

      {customer.stripe_customer_id && (
        <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
          <StripeCustomerLink
            customerId={customer.stripe_customer_id}
            checkoutSessionId={data.orders.find((o) => o.stripe_checkout_session_id)?.stripe_checkout_session_id}
          />
          <span style={{ fontSize: 12, color: '#9ca3af', fontFamily: 'monospace' }}>
            {customer.stripe_customer_id}
          </span>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: 8,
        padding: 16,
      }}
    >
      <div style={{ fontSize: 11, color: '#6b7280', letterSpacing: 1, textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize: 18, fontFamily: 'Georgia, serif', marginTop: 6 }}>{value}</div>
    </div>
  );
}
